<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" :id="testName + '-title'">
        {{ testName }}
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="variant-detection-test-grid"
      >
        <div class="mt-2 mb-6 mr-4">
          <p>
            Most familial variant testing is performed via targeted exome. If
            WGS is required, contact us at
            <a href="mailto:VCGSgenomics@vcgs.org.au"
              >VCGSgenomics@vcgs.org.au</a
            >.
          </p>
          <p>
            Provenance checks are performed for all assays via SNP-ID. Positive
            control and second independent samples are not required. Requests to
            test second samples will incur another test fee.
          </p>
          <p>
            For familial variant testing of microarray results, please use the
            Microarray test.
          </p>
        </div>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Funding source*</span>
            <v-select
              item-disabled=""
              v-model="billTo"
              :items="billingOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.billTo'"
              outlined
              :rules="billingRule"
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="billTo === 'MEDICARE'">
          <v-col class="custom-col">
            <span class="text-title">Medicare item number*</span>
            <v-text-field
              v-model="medicareItemNumber"
              :id="testId + '.medicareItemNumber'"
              :rules="ruleMedicareItemNumber"
              outlined
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Turn around time*</span>
            <v-select
              :disabled="billTo === 'MEDICARE'"
              v-model="rapid"
              :items="tatOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.urgency'"
              class="mt-3 mb-3"
              outlined
              :rules="ruleValidBoolean"
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Family type*</span>
            <v-select
              :disabled="billTo === 'MEDICARE'"
              v-model="familyType"
              :items="familyTypes"
              :id="testId + '.familyType'"
              no-data-text="Select a turn around time first"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="familyType === trioFlag">
          <v-col class="custom-col">
            <FamilyMember
              style="grid-area: person1"
              title="Family member 1"
              :idPrefix="testId + '.fm0.'"
              ref="familyMember1"
              :patientdata="patientdata"
              :familytype="familyType"
              :populatekeys="[
                'surname',
                'country',
                'unitFlat',
                'streetAddress',
                'suburb',
                'postcode',
                'state',
              ]"
              testType="prenatal_parents"
            />
          </v-col>
        </v-row>
        <v-row v-if="familyType === trioFlag">
          <v-col class="custom-col">
            <FamilyMember
              style="grid-area: person2"
              title="Family member 2"
              :idPrefix="testId + '.fm1.'"
              ref="familyMember2"
              :patientdata="patientdata"
              :familytype="familyType"
              :populatekeys="[
                'surname',
                'country',
                'unitFlat',
                'streetAddress',
                'suburb',
                'postcode',
                'state',
              ]"
              testType="prenatal_parents"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Predictive test?*</span>
            <v-select
              v-model="predictiveTest"
              :items="predictiveYesNo"
              :rules="ruleValidBoolean"
              item-text="key"
              item-value="value"
              outlined
              :id="testId + '.predictiveTest'"
              hide-details="auto"
            ></v-select
          ></v-col>
        </v-row>
        <v-row v-if="predictiveTest === true">
          <v-col class="custom-col">
            <v-alert
              text
              outlined
              type="warning"
              icon="mdi-cash-usd-outline"
              class="mr-1 pl-0"
            >
              For Targeted Exome, a second sample is not required. If two
              independent samples are received for testing, an additional test
              fee will be incurred.
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title"
              >Family member previously tested at VCGS*</span
            >
            <v-select
              v-model="previouslyTested"
              :items="yesNoOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.familyPrevTested'"
              outlined
              hide-details="auto"
              :rules="ruleValidBoolean"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="previouslyTested == true">
          <v-col class="custom-col">
            <span class="text-title">VCGS sample ID</span>
            <v-text-field
              v-model="posCtrlSampleId"
              :id="testId + '.posCtrlSampleId'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint"
              >e.g. 24W000123. This is located in the top right section of our
              reports.</span
            >
          </v-col>
        </v-row>
        <v-row v-if="previouslyTested === true">
          <v-col class="custom-col">
            <span class="text-title">Family member name</span>
            <v-text-field
              v-model="posCtrlName"
              :id="testId + '.posCtrlName'"
              outlined
              hide-details="auto"
            ></v-text-field
          ></v-col>
        </v-row>
        <v-row v-if="previouslyTested === true">
          <v-col class="custom-col">
            Family member date of birth
            <v-menu
              v-model="familyMemberDOBMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="familyMemberDOBDisplay"
                  :id="testId + '.posCtrlBirthDate'"
                  v-on="on"
                  :rules="familyMemberDOBDisplay != '' ? ruleDate : []"
                  outlined
                  hide-details="auto"
                ></v-text-field>
              </template>
              <v-date-picker
                locale="en-au"
                :min="minDate"
                :max="maxDate"
                v-model="familyDOBVal"
                @input="familyMemberDOBMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <div
          class="mb-3 mt-3 mb-md-6 mt-md-8"
          v-if="
            this.$dataset.isUploadPreviousReportEnabled &&
              previouslyTested === false
          "
        >
          <UploadComponent
            :testId="testId"
            :testName="testName"
            :updateOnDestroy="true"
            @updateUploadObj="updateUploadObj"
            uploadType="previousReport"
            noAlert
          />
        </div>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title"
              >Relationship of this patient to positive control*</span
            >
            <v-text-field
              v-model="rhipToPosCtrl"
              :id="testId + '.rhipToPosCtrl'"
              outlined
              hide-details="auto"
              :rules="ruleNotNull"
            ></v-text-field>
            <span class="text-hint"
              >e.g. sibling, parent, aunt, etc.</span
            ></v-col
          >
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title"
              >Is the person being tested affected by the same condition?*</span
            >
            <v-text-field
              v-model="relationAffected"
              :id="testId + '.relationAffected'"
              outlined
              hide-details="auto"
              :rules="ruleNotNull"
            ></v-text-field>
            <span class="text-hint">e.g. mildly affected</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Number of SNVs to be tested*</span>
            <v-select
              v-model="numVariants"
              :items="numVariantsRange"
              :id="testId + '.numVariants'"
              :rules="ruleNotNull"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="numVariants">
          <v-col cols="12" v-for="n in numVariants" :key="n">
            <div class="font-weight-bold">Variant #{{ n }}</div>
            <div class="text-title">Gene & Variant Details</div>
            <v-text-field
              v-model="variantsTested[n - 1].details"
              :id="`${testId}.variant${n}`"
              outlined
              hide-details="auto"
              :rules="ruleVariantDetails"
            ></v-text-field>
            <span class="text-hint">e.g. DDX23 c.1912G>T</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Clinical notes</span>
            <v-textarea
              v-model="clinicalNotes"
              :id="testId + '.clinicalNotes'"
              outlined
              hide-details="auto"
              rows="2"
            ></v-textarea>
            <span class="text-hint"
              >e.g. clinical indication for testing, relevant family history,
              results of relevant prior genetic and non-genetic investigations,
              biochemistry, histopathology and imaging, etc.</span
            >
          </v-col>
        </v-row>
        <v-alert text color="info" class="pl-10" outlined>
          <!-- <p>
            Please email the payment authorisation form to vcgs@vcgs.org.au and
            include the 6 character order ID in the subject of your email.
          </p> -->
          Please note that depending on the complexities of the variant
          additional charges may apply.
        </v-alert>

        <TestPriceAlert
          :medicare="medicare"
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="familial_varient_next_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import DropDownMixin from "../../mixins/DropDownMixin";
import TestPriceAlert from "../common/TestPriceAlert";
import { useSectionStore } from "../../store/SectionStore";
import UploadComponent from "../upload/UploadComponent.vue";
import UploadMixin from "../../mixins/UploadMixin.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "../../event-bus";
import FamilyMember from "../FamilyMember.vue";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin, UploadMixin, RetrieveJSONMixin],
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    showReanalysisAlert: Boolean,
  },
  components: { TestPriceAlert, UploadComponent, FamilyMember },
  watch: {
    familyType() {
      if (this.familyType === "Trio") {
        // When it is Trio test, update selectedTrioTests in App component
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }
    },
    billTo() {
      // If billTo is MEDICARE, Medicare card no. and position no. become required in Patient screen
      EventBus.$emit(
        "medicareVariantDetectionStatus",
        this.billTo === "MEDICARE",
      );

      // If billTo is MEDICARE, Turn around time is set to Standard by default
      if (this.billTo === "MEDICARE") {
        this.rapid = false;
        this.familyType = "Singleton";
      }

      // If funding source is PATIENT/OTHER, the `Send bill to` in Bill details is auto selected as the corresponding option
      if (this.billTo === "PATIENT") {
        useSectionStore().deligateBillingAddress = "PATIENT";
      } else {
        useSectionStore().deligateBillingAddress = "OTHER";
      }
    },
    familyDOBVal: function() {
      this.familyMemberDOBDisplay = this.familyDOBVal
        ? this.formatDate(this.familyDOBVal)
        : "";
    },
  },

  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.variantDetectionPricing = await this.execRetrieveJson(
      "json/pricing/variant_detection.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById("variant-detection-test-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },

  data: () => ({
    rapid: null,
    variantDetectionPricing: null,
    urgentKit: false,
    urgentReason: "",
    previouslyTested: null,
    predictiveTest: null,
    predictiveYesNo: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    relationAffected: "",
    rhipToPosCtrl: "",
    tatOptions: [
      { key: "Standard", value: false },
      { key: "Rapid", value: true },
    ],
    posCtrlSampleId: "",
    clinicalNotes: "",
    numVariants: null,
    numVariantsRange: [1, 2, 3, 4, 5],
    posCtrlName: "",
    familyMemberDOBMenu: false,
    familyDOBVal: null,
    familyMemberDOBDisplay: "",
    minDate: "1801-01-01",
    maxDate: new Date().toJSON().slice(0, 10),
    billingOptions: [
      { key: "Medicare Eligible (Bulk Bill)", value: "MEDICARE" },
      { key: "Patient / Family", value: "PATIENT" },
      { key: "Clinical Service / Institution / Hospital", value: "OTHER" },
    ],
    billTo: "",
    medicareItemNumber: "",
    familyType: null,
    familyTypes: ["Singleton", "Trio"],
    trioFlag: "Trio",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    variantsTested: [
      { id: 1, details: "" },
      { id: 2, details: "" },
      { id: 3, details: "" },
      { id: 4, details: "" },
      { id: 5, details: "" },
    ],
  }),
  computed: {
    previouslyTestedKey() {
      if (this.previouslyTested === null) return null;

      return this.previouslyTested ? "previouslyTested" : "notPreviouslyTested";
    },
    tatKey() {
      if (this.rapid === null) return null;

      return this.rapid ? "rapid" : "standard";
    },
    variantNumKey() {
      if (!this.numVariants) return null;

      return `variant${this.numVariants}`;
    },
    price() {
      // Price is not determined until funding source, previously tested, variant number and family type are selected
      if (
        !this.billTo ||
        !this.previouslyTestedKey ||
        !this.variantNumKey ||
        !this.familyType
      )
        return null;

      // If billTo is MEDICARE, just return medicare price
      if (this.billTo === "MEDICARE") {
        const medicarePrice = this.variantDetectionPricing["medicare"][
          this.previouslyTestedKey
        ][this.tatKey][this.familyType][this.variantNumKey]["price"];

        this.$emit("toggleTotalPrice", medicarePrice, this.testId);

        this.$emit("displayBillingAddress", false);

        EventBus.$emit("updateShowVariantReviewAlert", false);

        return medicarePrice;
      }

      // If billTo is not MEDICARE and familyType is Singleton
      if (this.familyType === "Singleton") {
        const singletonPrice = this.variantDetectionPricing[
          this.previouslyTestedKey
        ][this.tatKey][this.familyType][this.variantNumKey]["price"];

        this.$emit("toggleTotalPrice", singletonPrice, this.testId);

        this.$emit("displayBillingAddress", false);

        EventBus.$emit("updateShowVariantReviewAlert", false);

        return singletonPrice;
      }

      // If billTo is not MEDICARE and familyType is Trio, the prices are TBC
      // We display price alert instead of price.
      this.$emit("toggleTotalPrice", 0, this.testId);
      EventBus.$emit("updateShowVariantReviewAlert", true);

      this.$emit("displayBillingAddress", true);
      return null;
    },
    testCode() {
      // testCode is not determined until funding source, previously tested, variant number and family type are selected
      if (
        !this.billTo ||
        !this.previouslyTestedKey ||
        !this.variantNumKey ||
        !this.familyType
      )
        return "";

      // If billTo is MEDICARE
      if (this.billTo === "MEDICARE") {
        return this.variantDetectionPricing["medicare"][
          this.previouslyTestedKey
        ][this.tatKey][this.familyType][this.variantNumKey]["testCode"];
      }

      // If billTo is not MEDICARE
      return this.variantDetectionPricing[this.previouslyTestedKey][
        this.tatKey
      ][this.familyType][this.variantNumKey]["testCode"];
    },
    medicare() {
      return this.billTo === "MEDICARE" ? true : false;
    },
    billingRule() {
      if (this.billTo === "MEDICARE") {
        if (
          this.patientdata["patientStatusContent"] ===
            "public_outpatient_public_hospital" ||
          this.patientdata["patientStatusContent"] === "public_public_hospital"
        ) {
          return ["You cannot order a Medicare test for a public patient."];
        }

        return [];
      } else {
        return this.ruleNotNull;
      }
    },
    finalVariantDetails() {
      const variantsTestedArr = this.variantsTested.filter(
        (variant) => variant.id <= this.numVariants,
      );

      const variantsTestedStrings = variantsTestedArr.map(
        (v) => `Variant #${v.id} Details: ${v.details}`,
      );

      return variantsTestedStrings;
    },
    quotedPrice() {
      if (this.familyType === this.trioFlag) {
        return "TBC";
      }

      return this.dollarPrice(this.price);
    },
  },

  methods: {
    rapidCost() {
      if (this.rapid) {
        return 200;
      } else return 0;
    },
    formatDate: function(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    reverseFormatDate: function(date) {
      if (!date) return null;
      const [day, month, year] = date.split("/");

      return `${year}-${month}-${day}`;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    isFormValid: function() {
      // If family type is Trio, we need to validate family member form
      if (
        this.familyType === this.trioFlag &&
        this.$refs.familyMember1.isFormValid() &&
        this.$refs.familyMember2.isFormValid()
      ) {
        return true;
      }

      return true;
    },
    transferData: function() {
      let familyDetails = [];
      //Ordering family member in the array so it stays consistantly [fm0, fm1]
      if (this.familyType === "Trio") {
        const orderEntries = Object.entries(this.$refs)
          .filter(
            ([familyKey]) => familyKey.substring(0, 12) === "familyMember",
          )
          .sort();
        for (let [, familyComponent] of orderEntries) {
          familyDetails.push(familyComponent.transferData());
        }
      }

      return {
        testType: this.testId,
        urgency: this.rapid, // Determined by Turn around time value
        urgent: this.rapid, // When `urgency` is true, `urgent` is true by default
        urgentReason: this.urgentReason,
        predictiveTest: this.predictiveTest,
        familyPrevTested: this.previouslyTested,
        posCtrlSampleId: this.posCtrlSampleId,
        relationAffected: this.relationAffected,
        numVariants: this.numVariants,
        clinicalNotes:
          this.clinicalNotes +
          `${
            this.medicareItemNumber
              ? " medicareItemNumber: " + this.medicareItemNumber
              : ""
          }`,
        quotedPrice: this.quotedPrice,
        testCode: this.testCode,
        medicare: this.medicare,
        rhipToPosCtrl: this.rhipToPosCtrl,
        posCtrlName: this.posCtrlName,
        posCtrlBirthDate: this.reverseFormatDate(this.familyMemberDOBDisplay),
        billTo: this.billTo,
        variantDetails: this.finalVariantDetails,
        family: familyDetails,
      };
    },
  },
  beforeDestroy() {
    this.$emit("updateShowVariantReviewAlert", false);
    EventBus.$emit("deselectTrioTest", this.testName);
  },
};
</script>
